import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import MakeReadyMap from '../components/MakeReadyMap';
import PointCloudViewer from '../components/PointCloudViewer';
import StreetViewPoles from '../components/StreetViewPoles';
import NetworkFlythrough from '../components/NetworkFlythrough';

type Roll = {
  n_segments: number; high: number; medium: number; low: number;
  total_ft: number; high_ft: number; total_usd: number;
  computed_at: string | null; canopy_source: string | null;
};
type Seg = {
  segment_id: string; length_ft: number; n_points: number;
  max_canopy_m: number; mean_canopy_m: number; pct_points_tall: number;
  max_slope_pct: number | null; pct_points_steep: number; risk_score: number;
  risk_class: 'low' | 'medium' | 'high'; est_makeready_usd: number;
};
type MR = { progress: any; rollup: Roll; segments: Seg[] };

const M2FT = 3.28084;
const fmt0 = (n: number) => Math.round(n).toLocaleString();
const usd = (n: number) => '$' + Math.round(n).toLocaleString();
const miles = (ft: number) => (ft / 5280).toFixed(1);
const CLR: Record<string, [string, string]> = {
  high: ['#fee2e2', '#b91c1c'], medium: ['#fef3c7', '#b45309'], low: ['#dcfce7', '#047857'],
};
const badge = (c: string) => {
  const [bg, fg] = CLR[c] || CLR.low;
  return <span style={{ background: bg, color: fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>{c}</span>;
};
const STAGE_LABEL: Record<string, string> = {
  loading: 'Loading aerial route…', canopy: 'Sampling canopy height (Meta 1 m / WorldCover)…',
  terrain: 'Sampling terrain slope (USGS 3DEP)…', scoring: 'Scoring segments…', done: 'Complete',
};

export default function MakeReadyRiskPage() {
  const qc = useQueryClient();
  const [pid, setPid] = useState<string>('');
  const [selSeg, setSelSeg] = useState<string>('');
  const [pcOpen, setPcOpen] = useState(false);
  const [svOpen, setSvOpen] = useState(false);
  const [flyOpen, setFlyOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [rateFlag, setRateFlag] = useState<number | null>(null);
  const rateTouched = useRef(false);

  const { data: projects = [] } = useQuery<any[]>({ queryKey: ['bead-projects'], queryFn: () => api.get('/bead/projects') });
  const mr = useQuery<MR>({
    queryKey: ['makeready', pid],
    queryFn: () => api.get(`/bead/projects/${pid}/makeready`),
    enabled: !!pid,
    refetchInterval: (q) => { const d = q.state.data as MR | undefined; const s = d?.progress?.stage; const has = (d?.rollup?.n_segments ?? 0) > 0; return (s && s !== 'done' && !has) ? 3000 : false; },
  });
  const run = useMutation({
    mutationFn: () => api.post(`/bead/projects/${pid}/makeready/run`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['makeready', pid] }),
  });
  const svScan = useQuery<any>({
    queryKey: ['sv-scan', pid],
    queryFn: () => api.get(`/bead/projects/${pid}/sv-scan`),
    enabled: !!pid,
    refetchInterval: (q) => { const s = (q.state.data as any)?.progress?.stage; return s && s !== 'done' ? 4000 : false; },
  });
  const svRun = useMutation({
    mutationFn: () => api.post(`/bead/projects/${pid}/sv-scan/run`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sv-scan', pid] }),
  });
  const poleEstQ = useQuery<any>({
    queryKey: ['pole-est', pid],
    queryFn: () => api.get(`/bead/projects/${pid}/makeready/pole-estimate`),
    enabled: !!pid,
    refetchInterval: () => { const s = (svScan.data as any)?.progress?.stage; return s && s !== 'done' ? 6000 : false; },
  });
  const profileQ = useQuery<any>({
    queryKey: ['mr-profile', selSeg],
    queryFn: () => api.get(`/bead/projects/${pid}/makeready/profile?segment_id=${selSeg}`),
    enabled: !!pid && !!selSeg,
  });

  const roll = mr.data?.rollup;
  const segs = mr.data?.segments ?? [];
  const stage = mr.data?.progress?.stage as string | undefined;
  const running = !!stage && stage !== 'done' && !(roll && roll.n_segments > 0);
  const scored = !!roll && roll.n_segments > 0;
  const poleEst = poleEstQ.data;
  const effRate = rateFlag ?? Number(poleEst?.pole_rate ?? 350);
  const poleMr = poleEst?.scanned ? poleEst.flagged * effRate : 0;
  useEffect(() => { if (!rateTouched.current && poleEst?.pole_rate != null) setRateFlag(Number(poleEst.pole_rate)); }, [poleEst?.pole_rate]);

  return (
    <div style={{ padding: 20, maxWidth: 1100 }}>
      {pcOpen && selSeg && <PointCloudViewer segmentId={selSeg} onClose={() => setPcOpen(false)} />}
      {flyOpen && pid && <NetworkFlythrough projectId={pid} onClose={() => setFlyOpen(false)} />}
      {svOpen && selSeg && <StreetViewPoles segmentId={selSeg} onClose={() => setSvOpen(false)} />}
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>🌲 Make-Ready Risk</h1>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, maxWidth: 800, lineHeight: 1.5 }}>
        Planning-grade aerial make-ready risk per route segment, from free public LiDAR/EO data: <b>canopy height</b> (Meta
        1 m Global Canopy Height, ESA WorldCover fallback) flags tree-trim / clearance work, and <b>terrain slope</b>
        (USGS 3DEP) flags difficult access. Use it to price make-ready risk into a bid before design. Cost figures are
        estimates from per-foot adders, not a substitute for a field make-ready survey.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <select value={pid} onChange={(e) => { setPid(e.target.value); setSelSeg(''); }} style={{ padding: '7px 10px', fontSize: 14, borderRadius: 6, border: '1px solid var(--border)', minWidth: 280 }}>
          <option value="">Select a project…</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.project_code ?? p.id}</option>)}
        </select>
        {(!scored || running) ? (
          <button
            onClick={() => run.mutate()}
            disabled={!pid || running || run.isPending}
            style={{ padding: '7px 16px', fontSize: 14, fontWeight: 700, background: !pid || running ? '#94a3b8' : '#4338ca', color: '#fff', border: 'none', borderRadius: 6, cursor: !pid || running ? 'default' : 'pointer' }}>
            {running ? 'Scoring…' : run.isPending ? 'Starting…' : 'Run risk scoring'}
          </button>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Risk scored{roll?.computed_at ? ' ' + new Date(roll.computed_at).toLocaleDateString() : ''} ·{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); run.mutate(); }} style={{ color: '#6366f1' }}>↻ re-score</a>
          </span>
        )}
      </div>

      {run.isError && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{(run.error as any)?.message ?? 'Run failed'}</div>}

      {running && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', padding: '12px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          ⏳ {STAGE_LABEL[stage!] ?? stage}
          {mr.data?.progress?.n_segments ? <span style={{ color: '#64748b' }}> · {fmt0(mr.data.progress.n_segments)} aerial segments</span> : null}
        </div>
      )}

      {pid && !scored && !running && (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0' }}>
          Not yet scored. Click <b>Run scoring</b> — it samples canopy and terrain along every aerial segment (a couple of minutes for a large project).
        </div>
      )}

      {scored && roll && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 12 }}>
            <Card label="Poles on route" value={poleEst?.scanned ? `≈ ${fmt0(poleEst.est_poles)}` : `${miles(roll.total_ft)} mi`} sub={poleEst?.scanned ? `along ${miles(roll.total_ft)} aerial mi` : `${fmt0(roll.n_segments)} segments`} />
            {poleEst?.scanned
              ? <Card label="Detected (Street View)" value={fmt0(poleEst.detected)} sub={`${poleEst.coverage_pct}% of route covered`} />
              : <Card label="High-risk route" value={`${miles(roll.high_ft)} mi`} sub="canopy / slope" color="#b91c1c" />}
            {poleEst?.scanned
              ? <Card label="Flagged for review" value={fmt0(poleEst.flagged)} sub="transformer / heavy congestion" color="#b45309" />
              : <Card label="Est. make-ready" value={usd(roll.total_usd)} sub="canopy / slope estimate" color="#b45309" />}
            <Card label="Vegetation make-ready" value={`${miles(roll.high_ft)} mi`} sub={`high-risk · ${usd(roll.total_usd)} canopy est`} color="#16a34a" />
          </div>
          {poleEst?.scanned && (
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 12, color: 'var(--text-muted)' }}>
              <b style={{ color: 'var(--text)' }}>Pole make-ready (rough)</b>
              <label>$ / flagged pole <input type="number" value={effRate} onChange={(e) => { rateTouched.current = true; setRateFlag(Number(e.target.value) || 0); }} style={{ width: 90, marginLeft: 4, padding: '2px 4px' }} /></label>
              <span>= <b style={{ color: '#b45309', fontSize: 14 }}>{usd(poleMr)}</b> on {fmt0(poleEst.flagged)} flagged poles</span>
              <span style={{ color: 'var(--text-subtle,#94a3b8)' }}>Street View pole INVENTORY + coarse congestion flag (transformer / heavy load) — for triage + planning, not a clearance grade. {fmt0(poleEst.detected)} poles photographed across {fmt0(poleEst.panos)} panoramas. Pole rate defaults from your Rate Card; vegetation/access rates live there too.</span>
            </div>
          )}

          {(() => {
            const prog = svScan.data?.progress; const sum = svScan.data?.summary;
            const stg = prog?.stage; const scanning = !!stg && stg !== 'done';
            const stageLabel: any = { route: 'Reading aerial route…', panos: 'Finding Street View panoramas…', haiku: `Scanning poles (Haiku) ${prog?.done ?? 0}/${prog?.total ?? '?'}`, opus: `Verifying flagged poles (Opus) ${prog?.done ?? 0}/${prog?.total ?? '?'}` };
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                <button onClick={() => svRun.mutate()} disabled={scanning || svRun.isPending}
                  style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, background: scanning ? '#94a3b8' : '#0369a1', color: '#fff', border: 'none', borderRadius: 6, cursor: scanning ? 'default' : 'pointer' }}>
                  {scanning ? 'Scanning…' : svRun.isPending ? 'Starting…' : '🛰 Scan all poles (territory)'}
                </button>
                {scanning && <span style={{ fontSize: 12, color: '#1e3a8a' }}>⏳ {stageLabel[stg!] ?? stg}</span>}
                {sum && sum.panos > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sum.poles} poles · {sum.panos} panoramas · {sum.mr} make-ready flags · click pole dots on the map</span>}
                <span style={{ fontSize: 11, color: 'var(--text-subtle,#94a3b8)' }}>Street View AI screening — public roads only.</span>
              </div>
            );
          })()}
          <div style={{ marginBottom: 10 }}>
            <button onClick={() => setFlyOpen(true)} style={{ padding: '9px 18px', fontSize: 14, fontWeight: 800, background: '#4338ca', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', boxShadow: '0 2px 8px rgba(67,56,202,0.4)' }}>🎬 Cinematic fly-through</button>
          </div>
          <MakeReadyMap projectId={pid} onSelectSegment={setSelSeg} />

          {selSeg && (
            <div style={{ marginBottom: 20, border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Segment profile <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{selSeg.slice(0, 8)}</span></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={() => setPcOpen(true)} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🛰 LiDAR point cloud</button>
                  <button onClick={() => setSvOpen(true)} style={{ background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🛰 Street View poles</button>
                  <button onClick={() => setSelSeg('')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}>✕ close</button>
                </div>
              </div>
              {profileQ.isLoading ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading ground + canopy profile…</div> : <ProfileChart data={profileQ.data} />}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                <span style={{ color: '#a1764b' }}>▬</span> ground (3DEP) &nbsp; <span style={{ color: '#15803d' }}>▮</span> canopy &nbsp; <span style={{ color: '#1e3a8a' }}>┄</span> wire ≈ 30 ft attach. Where canopy crosses the wire line, expect trim / make-ready.
              </div>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '6px 8px' }}>Segment</th>
                <th style={{ padding: '6px 8px' }}>Length</th>
                <th style={{ padding: '6px 8px' }}>Max canopy</th>
                <th style={{ padding: '6px 8px' }}>% tall</th>
                <th style={{ padding: '6px 8px' }}>Max slope</th>
                <th style={{ padding: '6px 8px' }}>% steep</th>
                <th style={{ padding: '6px 8px' }}>Risk</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Est. $</th>
              </tr>
            </thead>
            <tbody>
              {(showAll ? segs : segs.slice(0, 25)).map((s) => (
                <tr key={s.segment_id} onClick={() => setSelSeg(s.segment_id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selSeg === s.segment_id ? '#eef2ff' : undefined }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{s.segment_id.slice(0, 8)}</td>
                  <td style={{ padding: '6px 8px' }}>{fmt0(s.length_ft)} ft</td>
                  <td style={{ padding: '6px 8px' }}>{Math.round(s.max_canopy_m * M2FT)} ft</td>
                  <td style={{ padding: '6px 8px' }}>{Math.round(s.pct_points_tall * 100)}%</td>
                  <td style={{ padding: '6px 8px' }}>{s.max_slope_pct != null ? Math.round(s.max_slope_pct) + '%' : '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{Math.round(s.pct_points_steep * 100)}%</td>
                  <td style={{ padding: '6px 8px' }}>{badge(s.risk_class)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{usd(s.est_makeready_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {segs.length > 25 && (
            <button onClick={() => setShowAll(!showAll)} style={{ marginTop: 8, background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              {showAll ? 'Show top 25 only' : `Show all ${segs.length} segments`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function Card({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', background: 'var(--surface)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color ?? 'var(--text)', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ProfileChart({ data }: { data: any }) {
  if (!data) return null;
  const canopy: any[] = data.canopy ?? [];
  const ground: any[] = data.ground ?? [];
  const hav = (a: any, b: any) => {
    const R = 6371000, p1 = a.lat * Math.PI / 180, p2 = b.lat * Math.PI / 180;
    const dp = (b.lat - a.lat) * Math.PI / 180, dl = (b.lon - a.lon) * Math.PI / 180;
    const x = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
  };
  const cum = (arr: any[]) => { let d = 0; return arr.map((p, i) => { if (i > 0) d += hav(arr[i - 1], p); return { ...p, d }; }); };
  const g = cum(ground), c = cum(canopy);
  if (g.length < 2 && c.length < 2) return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No profile data for this segment.</div>;
  const maxD = Math.max(g.length ? g[g.length - 1].d : 0, c.length ? c[c.length - 1].d : 0) || 1;
  const groundAt = (d: number) => {
    if (!g.length) return 0;
    if (d <= g[0].d) return g[0].e;
    for (let i = 1; i < g.length; i++) { if (d <= g[i].d) { const t = (d - g[i - 1].d) / ((g[i].d - g[i - 1].d) || 1); return g[i - 1].e + t * (g[i].e - g[i - 1].e); } }
    return g[g.length - 1].e;
  };
  const tops = c.map((p) => ({ d: p.d, gnd: groundAt(p.d), top: groundAt(p.d) + Number(p.canopy_m || 0) }));
  const elevs = [...g.map((p) => p.e), ...tops.map((t) => t.top)];
  let minE = Math.min(...elevs), maxE = Math.max(...elevs); if (minE === maxE) maxE = minE + 1;
  const W = 800, H = 220, padL = 44, padB = 22, padT = 10, padR = 12;
  const X = (d: number) => padL + (d / maxD) * (W - padL - padR);
  const Y = (e: number) => padT + (1 - (e - minE) / (maxE - minE)) * (H - padT - padB);
  const POLE = 9;
  const gp = g.length ? g.map((p, i) => `${i ? 'L' : 'M'}${X(p.d).toFixed(1)},${Y(p.e).toFixed(1)}`).join(' ') : '';
  const gArea = g.length ? gp + ` L${X(g[g.length - 1].d).toFixed(1)},${H - padB} L${padL},${H - padB} Z` : '';
  const ct = tops.length ? tops.map((p, i) => `${i ? 'L' : 'M'}${X(p.d).toFixed(1)},${Y(p.top).toFixed(1)}`).join(' ') : '';
  const wire = g.length ? g.map((p, i) => `${i ? 'L' : 'M'}${X(p.d).toFixed(1)},${Y(p.e + POLE).toFixed(1)}`).join(' ') : '';
  const band = tops.length ? ('M' + tops.map((p) => `${X(p.d).toFixed(1)},${Y(p.top).toFixed(1)}`).join(' L') + ' ' + tops.slice().reverse().map((p) => `L${X(p.d).toFixed(1)},${Y(p.gnd).toFixed(1)}`).join(' ') + ' Z') : '';
  const crossings = tops.filter((p) => (p.top - p.gnd) > POLE);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 220, background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border)' }}>
      {gArea && <path d={gArea} fill="#e7d8bf" opacity={0.85} />}
      {band && <path d={band} fill="#22c55e" opacity={0.45} />}
      {ct && <path d={ct} fill="none" stroke="#15803d" strokeWidth={1.2} opacity={0.85} />}
      {crossings.map((p, i) => <circle key={`x${i}`} cx={X(p.d)} cy={Y(p.top)} r={3} fill="#dc2626" />)}
      {gp && <path d={gp} fill="none" stroke="#a1764b" strokeWidth={1.8} />}
      {wire && <path d={wire} fill="none" stroke="#1e3a8a" strokeWidth={1.6} strokeDasharray="5 3" />}
      <text x={padL} y={H - 5} fontSize={10} fill="#64748b">0</text>
      <text x={W - padR} y={H - 5} fontSize={10} fill="#64748b" textAnchor="end">{Math.round(maxD)} m</text>
      <text x={4} y={Y(maxE) + 8} fontSize={10} fill="#64748b">{Math.round(maxE)}m</text>
      <text x={4} y={Y(minE)} fontSize={10} fill="#64748b">{Math.round(minE)}m</text>
    </svg>
  );
}
